import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Icon from '@material-ui/core/Icon';
import Paper from '@material-ui/core/Paper';
import Tooltip from '@material-ui/core/Tooltip';
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import AddPaymentMethodDialog from 'v2/components/Shared/AddPaymentMethodDialog';
import GlobalLoading from 'v2/components/Shared/GlobalLoading';
import ListCards from 'v2/components/Shared/SelectCreditCard/ListCards';
import UnloggedLayout from 'v2/components/Shared/UnloggedLayout';
import changePaymentMethodService from 'v2/services/changePaymentMethod';

import type { ICard } from 'v2/components/Shared/SelectCreditCard';
import type { ICardApiResponse } from 'v2/interfaces/customer';
import type { ISubscriptions } from 'v2/interfaces/subscriptions';

import ConfirmDialog from './ConfirmDialog';
import ContractCard from './ContractCard';
import PaymentChanged from './PaymentChanged';
import { useStyles } from './styles';
import TokenExpired from './TokenExpired';

import type { FormikProps } from 'formik';

const ChangePaymentMethod = () => {
  const classes = useStyles();
  const [selectedCard, setSelectedCard] = useState<ICard>();
  const [isAddCardDialogOpen, setIsAddCardDialongOpen] = useState(false);
  const [cardsList, setCardsList] = useState([] as ICardApiResponse[]);
  const [contract, setContract] = useState({} as ISubscriptions);
  const [pageError, setPageError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [changing, setChanging] = useState(false);
  const [changed, setChanged] = useState(false);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const token = qs.get('token');

    (async () => {
      try {
        changePaymentMethodService.setToken(token);
        const cards = await changePaymentMethodService.getCards();
        const contract = await changePaymentMethodService.getSubscription();
        setCardsList(cards);
        setContract(contract);
      } catch (err) {
        setPageError(true);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onClickCard = useCallback((card: ICard) => {
    setSelectedCard(card);
  }, []);

  const handleOpenAddCardDialog = useCallback(() => {
    setIsAddCardDialongOpen(true);
  }, []);

  const handleCloseAddCardDialog = useCallback(() => {
    setIsAddCardDialongOpen(false);
  }, []);

  const handleNewCard = useCallback(
    (values: ICard, { setSubmitting, resetForm }: FormikProps<ICard>) => {
      setSubmitting(true);
      changePaymentMethodService
        .saveNewCard(values)
        .then((response) => {
          setSubmitting(false);
          handleCloseAddCardDialog();
          resetForm();
          setCardsList((prevState) => [response.data.data, ...prevState]);
          toast.success('Forma de pagamento cadastrada com sucesso!');
        })
        .catch((err) => {
          setSubmitting(false);
          toast.error(err);
        });
    },
    [handleCloseAddCardDialog]
  );

  const handlePaymentChange = useCallback((paymentId: number) => {
    setChanging(true);
    changePaymentMethodService
      .changePayment(paymentId)
      .then(() => {
        setChanging(false);
        setChanged(true);
        toast.success('Forma de pagamento alterada com sucesso!');
      })
      .catch((err) => {
        setChanging(false);
        toast.error(err);
      });
  }, []);

  if (loading) return <GlobalLoading />;
  if (pageError) return <TokenExpired />;
  if (changed) return <PaymentChanged contract={contract} card={selectedCard} />;

  return (
    <UnloggedLayout>
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        card={selectedCard}
        handleClose={() => setIsConfirmDialogOpen(false)}
        onSubmit={() => handlePaymentChange(selectedCard.id)}
        isChanging={changing}
      />
      <Grid container direction='column'>
        <Grid container justify='space-between' alignItems='flex-end'>
          <div className='my-title'>
            <h1>Alterar dados de pagamento</h1>
            <p>Escolha uma nova forma de pagamento para o contrato abaixo</p>
          </div>
        </Grid>

        <div className='m24' />
        <Grid container justify='space-between'>
          <ContractCard contract={contract} />
          <div className={classes.pageActions}>
            <Button
              variant='contained'
              color='secondary'
              onClick={handleOpenAddCardDialog}
              className={classes.newCardBtn}
              startIcon={<Icon>add</Icon>}
            >
              Novo cartão
            </Button>
            <Tooltip
              title='Selecione um cartão'
              placement='top'
              disableFocusListener={!!selectedCard}
              disableHoverListener={!!selectedCard}
              disableTouchListener={!!selectedCard}
            >
              <div>
                <Button
                  variant='contained'
                  color='primary'
                  onClick={() => setIsConfirmDialogOpen(true)}
                  startIcon={changing ? <CircularProgress size={10} style={{ color: '#fff' }} /> : <Icon>autorenew</Icon>}
                  disabled={!selectedCard || changing}
                >
                  Trocar cartão
                </Button>
              </div>
            </Tooltip>
          </div>
        </Grid>
        <div className='m24' />
        <Paper elevation={1} className={classes.cards}>
          <ListCards cardsList={cardsList} selectedCard={selectedCard} onCardClick={onClickCard} />
        </Paper>

        <AddPaymentMethodDialog isOpen={isAddCardDialogOpen} onClose={handleCloseAddCardDialog} onSubmit={handleNewCard} />
      </Grid>
    </UnloggedLayout>
  );
};

export default ChangePaymentMethod;
